import React, { useEffect, useMemo, useRef, useState } from 'react';
import type { ChatMessage, NoraArtifact } from '../types';
import { COLOR, FONT } from '../styles/tokens';

const FENCE = /```([^\n`]*)\n([\s\S]*?)```/g;
const SUPPORTED = new Set([
  'yaml',
  'yml',
  'json',
  'promql',
  'prometheus',
  'rollout',
  'runbook',
  'markdown',
  'md',
]);

export function extractArtifacts(messages: ChatMessage[]): NoraArtifact[] {
  return messages.flatMap(message => {
    if (message.role !== 'assistant') return [];
    const artifacts: NoraArtifact[] = [];
    let match: RegExpExecArray | null;
    let index = 0;
    FENCE.lastIndex = 0;
    while ((match = FENCE.exec(message.text)) !== null) {
      const descriptor = match[1].trim();
      const language = descriptor.split(/\s+/)[0].toLowerCase() || 'text';
      const lowerDescriptor = descriptor.toLowerCase();
      const content = match[2].replace(/\n$/, '');
      const looksLikePlan =
        lowerDescriptor.includes('runbook') ||
        lowerDescriptor.includes('rollout') ||
        /^(runbook|rollout plan)\s*:/im.test(content);
      if (SUPPORTED.has(language) || looksLikePlan) {
        const kind = lowerDescriptor.includes('runbook')
          ? 'Runbook'
          : lowerDescriptor.includes('rollout')
          ? 'Rollout plan'
          : language === 'promql' || language === 'prometheus'
          ? 'PromQL'
          : language === 'json'
          ? 'JSON'
          : language === 'yaml' || language === 'yml'
          ? 'YAML'
          : 'Runbook';
        artifacts.push({
          id: `${message.id}:${index}`,
          messageId: message.id,
          language,
          title: `${kind} ${index + 1}`,
          original: content,
        });
      }
      index += 1;
    }
    return artifacts;
  });
}

function diffLines(original: string, current: string) {
  const before = original.split('\n');
  const after = current.split('\n');
  const rows = Math.max(before.length, after.length);
  return Array.from({ length: rows }, (_, index) => ({
    index,
    before: before[index],
    after: after[index],
    changed: before[index] !== after[index],
  }));
}

export function OperationsCanvas({
  artifacts,
  open,
  onClose,
  isMobile,
}: {
  artifacts: NoraArtifact[];
  open: boolean;
  onClose: () => void;
  isMobile: boolean;
}) {
  const [selectedId, setSelectedId] = useState('');
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [showDiff, setShowDiff] = useState(false);
  const [width, setWidth] = useState(440);
  const dragging = useRef(false);

  useEffect(() => {
    if (!artifacts.some(artifact => artifact.id === selectedId)) {
      setSelectedId(artifacts[0]?.id ?? '');
    }
  }, [artifacts, selectedId]);

  useEffect(() => {
    const move = (event: MouseEvent) => {
      if (!dragging.current) return;
      setWidth(Math.max(320, Math.min(720, window.innerWidth - event.clientX)));
    };
    const stop = () => {
      dragging.current = false;
    };
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', stop);
    return () => {
      window.removeEventListener('mousemove', move);
      window.removeEventListener('mouseup', stop);
    };
  }, []);

  const selected = artifacts.find(artifact => artifact.id === selectedId);
  const current = selected ? drafts[selected.id] ?? selected.original : '';
  const lines = useMemo(
    () => (selected ? diffLines(selected.original, current) : []),
    [current, selected],
  );

  if (!open) return null;

  const download = () => {
    if (!selected) return;
    const extension =
      selected.language === 'yml' ? 'yaml' : selected.language || 'txt';
    const url = URL.createObjectURL(
      new Blob([current], { type: 'text/plain' }),
    );
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `nora-${selected.title
      .toLowerCase()
      .replace(/\W+/g, '-')}.${extension}`;
    anchor.click();
    URL.revokeObjectURL(url);
  };

  return (
    <aside
      aria-label="Operations Canvas"
      style={{
        position: 'relative',
        width: isMobile ? '100%' : width,
        height: isMobile ? '52%' : '100%',
        flexShrink: 0,
        display: 'flex',
        flexDirection: 'column',
        borderLeft: isMobile ? 'none' : `1px solid ${COLOR.border}`,
        borderTop: isMobile ? `1px solid ${COLOR.border}` : 'none',
        background: COLOR.bgRaised,
        color: COLOR.textPrimary,
      }}
    >
      {!isMobile && (
        <div
          role="separator"
          aria-label="Resize Operations Canvas"
          onMouseDown={() => {
            dragging.current = true;
          }}
          style={{
            position: 'absolute',
            left: -4,
            insetBlock: 0,
            width: 8,
            cursor: 'col-resize',
            zIndex: 2,
          }}
        />
      )}
      <div
        style={{
          minHeight: 48,
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: '0 12px',
          borderBottom: `1px solid ${COLOR.borderSubtle}`,
        }}
      >
        <strong style={{ flex: 1 }}>Operations Canvas</strong>
        <button type="button" onClick={onClose} style={textButton}>
          Close
        </button>
      </div>
      {artifacts.length === 0 ? (
        <div style={{ padding: 18, color: COLOR.textMuted }}>
          YAML, JSON, PromQL, rollout plans, and runbooks from Nora will appear
          here.
        </div>
      ) : (
        <>
          <div
            className="nora-scroll"
            style={{
              display: 'flex',
              gap: 6,
              padding: 10,
              overflowX: 'auto',
            }}
          >
            {artifacts.map(artifact => (
              <button
                key={artifact.id}
                type="button"
                onClick={() => setSelectedId(artifact.id)}
                style={{
                  ...chipButton,
                  background:
                    artifact.id === selectedId
                      ? COLOR.bgOverlay
                      : 'transparent',
                }}
              >
                {artifact.title}
              </button>
            ))}
          </div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              padding: '0 10px 8px',
            }}
          >
            <span
              style={{
                flex: 1,
                color: COLOR.textMuted,
                fontSize: FONT.size.xs,
              }}
            >
              Local draft · Kubernetes is never changed from this canvas
            </span>
            <button
              type="button"
              onClick={() => navigator.clipboard.writeText(current)}
              style={textButton}
            >
              Copy
            </button>
            <button type="button" onClick={download} style={textButton}>
              Download
            </button>
            <button
              type="button"
              onClick={() => setShowDiff(value => !value)}
              style={textButton}
            >
              {showDiff ? 'Edit' : 'Diff'}
            </button>
          </div>
          {showDiff && selected ? (
            <div className="nora-scroll" style={{ flex: 1, overflow: 'auto' }}>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  position: 'sticky',
                  top: 0,
                  background: COLOR.bgOverlay,
                  fontSize: FONT.size.xs,
                }}
              >
                <strong style={{ padding: 7 }}>Original</strong>
                <strong style={{ padding: 7 }}>Current</strong>
              </div>
              {lines.map(line => (
                <div
                  key={line.index}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    fontFamily: FONT.mono,
                    fontSize: FONT.size.xs,
                  }}
                >
                  <pre
                    style={{
                      margin: 0,
                      padding: '2px 7px',
                      whiteSpace: 'pre-wrap',
                      color: line.changed
                        ? COLOR.diffRemoved
                        : COLOR.textSecondary,
                      background: line.changed
                        ? COLOR.diffRemovedBg
                        : 'transparent',
                    }}
                  >
                    {line.before ?? ' '}
                  </pre>
                  <pre
                    style={{
                      margin: 0,
                      padding: '2px 7px',
                      whiteSpace: 'pre-wrap',
                      color: line.changed
                        ? COLOR.diffAdded
                        : COLOR.textSecondary,
                      background: line.changed
                        ? COLOR.diffAddedBg
                        : 'transparent',
                    }}
                  >
                    {line.after ?? ' '}
                  </pre>
                </div>
              ))}
            </div>
          ) : (
            <textarea
              aria-label={`Edit ${selected?.title ?? 'artifact'}`}
              value={current}
              onChange={event =>
                selected &&
                setDrafts(value => ({
                  ...value,
                  [selected.id]: event.target.value,
                }))
              }
              spellCheck={false}
              style={{
                flex: 1,
                minHeight: 0,
                padding: 12,
                resize: 'none',
                border: 0,
                borderTop: `1px solid ${COLOR.borderSubtle}`,
                outline: 'none',
                background: COLOR.bg,
                color: COLOR.textPrimary,
                fontFamily: FONT.mono,
                fontSize: FONT.size.sm,
                lineHeight: 1.55,
              }}
            />
          )}
        </>
      )}
    </aside>
  );
}

const textButton: React.CSSProperties = {
  padding: '5px 7px',
  border: 0,
  background: 'transparent',
  color: COLOR.blue,
  cursor: 'pointer',
  fontFamily: FONT.sans,
  fontSize: FONT.size.xs,
};

const chipButton: React.CSSProperties = {
  flexShrink: 0,
  padding: '6px 9px',
  border: `1px solid ${COLOR.borderSubtle}`,
  borderRadius: 10,
  color: COLOR.textSecondary,
  cursor: 'pointer',
  fontFamily: FONT.sans,
  fontSize: FONT.size.xs,
};
